import React, { useEffect, useState } from 'react';
import { useAuth } from '../../Authentication/auth';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    ArrowUpRight, LogOut,
    LayoutDashboard, PlusCircle, BarChart2, Wallet,
} from 'lucide-react';

const QUICK_LINKS = [
    { label: 'Dashboard', desc: 'Overview & analytics', icon: LayoutDashboard, to: '/dashboard', color: '#34d399' },
    { label: 'Add Entry', desc: 'Log income or expense', icon: PlusCircle, to: '/input', color: '#38bdf8' },
    { label: 'Reports', desc: 'Monthly breakdowns', icon: BarChart2, to: '/reports', color: '#f472b6' },
    { label: 'Budget', desc: 'Set spending limits', icon: Wallet, to: '/budget', color: '#facc15' },
];

// Seeded positions — stable across re-renders
const TICKERS = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    value: `$${((i * 743.17 + 291.5) % 9999).toFixed(2)}`,
    x: ((i * 17 + 5) % 90) + 5,
    y: ((i * 23 + 8) % 90) + 5,
    delay: (i * 0.4) % 6,
}));

export default function Home() {
    const { user, logout } = useAuth();
    const [mounted, setMounted] = useState(false);

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 60);
        return () => clearTimeout(t);
    }, []);

    const handleLogout = async () => {
        try { await logout(); } catch (e) { console.error(e); }
    };

    const name = user?.displayName || user?.name || 'there';
    const firstName = name.split(' ')[0];

    return (
        <div className="relative min-h-screen bg-[#080b10] overflow-hidden flex flex-col items-center justify-center px-4 py-16">
            <Helmet><title>Home — Expense Tracker</title></Helmet>

            {/* Dot-grid background */}
            <div className="bg-grid absolute inset-0 pointer-events-none" />

            {/* Glow orbs */}
            <div className="orb w-[520px] h-[520px] bg-emerald-500/10 top-[-120px] left-[-100px]" />
            <div className="orb w-[400px] h-[400px] bg-sky-500/[0.08] bottom-[-80px] right-[-80px]" />

            {/* Floating ticker numbers */}
            <div className="absolute inset-0 pointer-events-none overflow-hidden select-none">
                {TICKERS.map(({ id, value, x, y, delay }) => (
                    <span
                        key={id}
                        className="absolute text-[11px] text-slate-800 font-mono animate-float-num"
                        style={{ left: `${x}%`, top: `${y}%`, animationDelay: `${delay}s` }}
                    >
                        {value}
                    </span>
                ))}
            </div>

            {/* Content */}
            <div className="relative z-10 w-full max-w-2xl flex flex-col items-center gap-10">

                {/* Greeting */}
                <div
                    className={`text-center space-y-4 transition-all duration-700 ease-out
            ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                    style={{ transitionDelay: '0ms' }}
                >
                    <p className="text-xs text-slate-600 uppercase tracking-[0.25em] font-mono">
                        expense tracker
                    </p>
                    <h1 className="font-syne text-5xl sm:text-6xl font-extrabold text-slate-100 leading-tight">
                        Hey,{' '}
                        <Link to="/profile" className="name-shimmer hover:opacity-80 transition-opacity" title="View profile">
                            {firstName}
                        </Link>
                        <span className="ml-2 inline-block origin-[70%_70%] animate-wave">👋</span>
                    </h1>
                    <p className="text-slate-500 text-lg leading-relaxed max-w-md mx-auto">
                        Your financial command centre.{' '}
                        <span className="text-slate-400">Track every dollar, understand every pattern.</span>
                    </p>
                </div>

                {/* Divider */}
                <div
                    className={`w-full h-line transition-all duration-700 ease-out
            ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                    style={{ transitionDelay: '100ms' }}
                />

                {/* Quick-link cards */}
                <div
                    className={`w-full grid grid-cols-2 gap-3 transition-all duration-700 ease-out
            ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                    style={{ transitionDelay: '200ms' }}
                >
                    {QUICK_LINKS.map(({ label, desc, icon: Icon, to, color }) => (
                        <Link
                            key={label}
                            to={to}
                            className="ql-card p-5 flex items-start gap-4 group"
                            style={{ '--card-color': color }}
                        >
                            <div className="mt-0.5 p-2.5 rounded-xl shrink-0" style={{ background: `${color}18`, color }}>
                                <Icon size={18} />
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-1.5">
                                    <span className="font-syne text-slate-200 font-semibold text-sm">{label}</span>
                                    <ArrowUpRight size={12} className="text-slate-600 group-hover:text-slate-400 transition-colors shrink-0" />
                                </div>
                                <p className="text-slate-600 text-xs mt-0.5">{desc}</p>
                            </div>
                        </Link>
                    ))}
                </div>

                {/* Divider */}
                <div
                    className={`w-full h-line transition-all duration-700 ease-out
            ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                    style={{ transitionDelay: '300ms' }}
                />

                {/* Footer */}
                <div
                    className={`w-full flex items-center justify-between transition-all duration-700 ease-out
            ${mounted ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
                    style={{ transitionDelay: '400ms' }}
                >
                    <span className="text-xs text-slate-700 font-mono">
                        Signed in as <span className="text-slate-500">{user?.email || name}</span>
                    </span>
                    <button
                        onClick={handleLogout}
                        className="logout-btn flex items-center gap-2 text-xs text-slate-600 px-3 py-2 rounded-xl font-mono"
                    >
                        <LogOut size={13} />
                        Sign out
                    </button>
                </div>

            </div>
        </div>
    );
}